import { CSSProperties } from 'react';
import { css } from '@emotion/react';
import { LoadingSkeleton } from '@frontend/assets';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, Truncated } from '@frontend/design-system';
import { InfoHoverIcon } from './info-hover-icon';

export interface StatItemData {
  editIconTooltipText?: string;
  infoHoverText?: string;
  isLoading?: boolean;
  label?: string;
  labelStyle?: CSSProperties;
  onEditClick?: () => void;
  style?: CSSProperties;
  value?: number | string;
}

export const StatItem = ({
  editIconTooltipText,
  infoHoverText,
  isLoading,
  label,
  labelStyle,
  onEditClick,
  value,
  ...rest
}: StatItemData) => {
  const { isSmallMode } = useIsSmallMode();

  return (
    <div css={styles.wrapper} data-testid='stat-item' {...rest}>
      {isLoading ? (
        <LoadingSkeleton css={styles.loading} data-testid='loader' />
      ) : (
        <div css={styles.valueWithEdit}>
          <Truncated
            as='span'
            className='value'
            style={{ fontSize: isSmallMode ? theme.font.size.large : theme.font.size.h1 }}
          >
            {value}
          </Truncated>
          {typeof onEditClick === 'function' && (
            <Button
              className='edit-icon'
              color='red'
              data-testid='edit-button'
              hoverLabel={editIconTooltipText}
              iconName='edit'
              onClick={onEditClick}
              size='small'
              variant='secondary'
            />
          )}
        </div>
      )}
      <div css={styles.labelWithTip}>
        <Truncated as='span' className='label' style={labelStyle}>
          {label}
        </Truncated>
        {infoHoverText && <InfoHoverIcon text={infoHoverText} />}
      </div>
    </div>
  );
};

const styles = {
  wrapper: css`
    max-width: 100%;

    span {
      display: block;
    }

    .value {
      font-weight: ${theme.font.weight.bold};
      line-height: 1;
      max-width: 100%;
      text-align: left;
      width: fit-content;
    }
  `,

  labelWithTip: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-start;
    min-width: 0; // Required to correctly truncate the text

    .label {
      color: ${theme.colors.neutral50};
      font-size: ${theme.font.size.small};
      max-width: 100%;
      text-align: left;
      width: auto;
    }
  `,

  loading: css`
    height: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(1)};
  `,

  valueWithEdit: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};

    .edit-icon {
      cursor: pointer;
      height: ${theme.spacing(2)};
      min-height: ${theme.spacing(2)};
      min-width: ${theme.spacing(2)};
      width: ${theme.spacing(2)};

      svg {
        color: ${theme.font.colors.subdued};
      }
    }
  `,
};
